import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import CustomModal from '@/components/atoms/CustomModal';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import InfoText from '@/components/atoms/InfoText';
import { setModalId, usePublicState } from '@/store/slices/publicSlice';
import {
  useGetAuditLogActionsQuery,
  useGetAuditLogFunctionsQuery,
  useGetAuditLogRolesQuery,
} from '@/store/services/configLogsService';

export interface LogsFilters {
  roles?: string[];
  functionNames?: string[];
  actions?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface FormValues {
  roles: string[];
  functionNames: string[];
  actions: string[];
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

export const LogsFilterModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId, modalExtraData } = usePublicState();

  const isModalOpen = modalId === 'CONFIG_LOGS_FILTERS';

  const { data: apiFunctions, isLoading: isLoadingFunctions } =
    useGetAuditLogFunctionsQuery(undefined, {
      skip: !isModalOpen,
    });

  const { data: apiActions, isLoading: isLoadingActions } =
    useGetAuditLogActionsQuery(undefined, {
      skip: !isModalOpen,
    });

  const { data: apiRoles, isLoading: isLoadingRoles } =
    useGetAuditLogRolesQuery(undefined, {
      skip: !isModalOpen,
    });

  const currentFilters = useMemo(() => {
    return (
      (modalExtraData as { configLogsFilters?: LogsFilters })
        ?.configLogsFilters || {}
    );
  }, [modalExtraData]);

  const methods = useForm<FormValues>({
    defaultValues: {
      roles: currentFilters.roles || [],
      functionNames: currentFilters.functionNames || [],
      actions: currentFilters.actions || [],
      dateFrom: currentFilters.dateFrom ? dayjs(currentFilters.dateFrom) : null,
      dateTo: currentFilters.dateTo ? dayjs(currentFilters.dateTo) : null,
    },
    values: {
      roles: currentFilters.roles || [],
      functionNames: currentFilters.functionNames || [],
      actions: currentFilters.actions || [],
      dateFrom: currentFilters.dateFrom ? dayjs(currentFilters.dateFrom) : null,
      dateTo: currentFilters.dateTo ? dayjs(currentFilters.dateTo) : null,
    },
  });

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    Boolean(watchAll.roles && watchAll.roles.length > 0),
    Boolean(watchAll.functionNames && watchAll.functionNames.length > 0),
    Boolean(watchAll.actions && watchAll.actions.length > 0),
    watchAll.dateFrom !== null && watchAll.dateFrom !== undefined,
    watchAll.dateTo !== null && watchAll.dateTo !== undefined,
  ].filter(Boolean).length;

  const onClose = () => {
    dispatch(setModalId({ id: null }));
  };

  const onReset = () => {
    methods.reset({
      roles: [],
      functionNames: [],
      actions: [],
      dateFrom: null,
      dateTo: null,
    });
  };

  const onSubmit = (values: FormValues) => {
    dispatch(
      setModalId({
        id: null,
        extraData: {
          ...(modalExtraData as object),
          configLogsFilters: {
            roles:
              values.roles && values.roles.length > 0
                ? values.roles
                : undefined,
            functionNames:
              values.functionNames && values.functionNames.length > 0
                ? values.functionNames
                : undefined,
            actions:
              values.actions && values.actions.length > 0
                ? values.actions
                : undefined,
            dateFrom: values.dateFrom
              ? values.dateFrom.format('YYYY-MM-DD')
              : undefined,
            dateTo: values.dateTo
              ? values.dateTo.format('YYYY-MM-DD')
              : undefined,
          },
        },
      })
    );
  };

  const roleOptions = useMemo(() => {
    return (apiRoles || []).map((item) => ({
      label: item.name,
      value: item.code,
    }));
  }, [apiRoles]);

  const functionOptions = useMemo(() => {
    return (apiFunctions || []).map((item) => ({
      label: item.name,
      value: item.code,
    }));
  }, [apiFunctions]);

  const actionOptions = useMemo(() => {
    return (apiActions || []).map((item) => ({
      label: item.name,
      value: item.code,
    }));
  }, [apiActions]);

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={onClose}
      title={t('home.filters')}
      subTitle={
        <div
          className="flex items-center gap-2"
          data-testid="config-logs-filters-subtitle-container"
        >
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
            data-testid="config-logs-filters-applied-count"
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
            data-testid="config-logs-filters-reset-btn"
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
      width={580}
      centered
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 pt-2"
          data-testid="config-logs-filters-form"
        >
          <div
            className="flex flex-col gap-2"
            data-testid="config-logs-roles-wrapper"
          >
            <FormField
              id="roles"
              name="roles"
              type="select"
              label={t('configLogs.role')}
              placeholder={t('home.searchHere')}
              mode="multiple"
              loading={isLoadingRoles}
              options={roleOptions}
              allowClear
              data-testid="config-logs-roles-checkbox"
            />
          </div>

          <div
            className="flex flex-col gap-2"
            data-testid="config-logs-function-names-wrapper"
          >
            <FormField
              id="functionNames"
              name="functionNames"
              type="select"
              label={t('configLogs.functionName')}
              placeholder={t('home.searchHere')}
              mode="multiple"
              loading={isLoadingFunctions}
              options={functionOptions}
              allowClear
              data-testid="config-logs-function-names-select"
            />
          </div>

          <div
            className="flex flex-col gap-2"
            data-testid="config-logs-actions-wrapper"
          >
            <FormField
              id="actions"
              name="actions"
              type="select"
              label={t('configLogs.action')}
              placeholder={t('home.searchHere')}
              mode="multiple"
              loading={isLoadingActions}
              options={actionOptions}
              allowClear
              data-testid="config-logs-actions-checkbox"
            />
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            data-testid="config-logs-dates-wrapper"
          >
            <FormField
              id="dateFrom"
              name="dateFrom"
              type="date"
              label={t('home.dateFrom')}
              placeholder="dd/mm/yyyy"
              disabledDate={(current: Dayjs) =>
                watchAll.dateTo
                  ? current.isAfter(watchAll.dateTo, 'day')
                  : false
              }
              data-testid="config-logs-date-from-picker"
            />
            <FormField
              id="dateTo"
              name="dateTo"
              type="date"
              label={t('home.dateTo')}
              placeholder="dd/mm/yyyy"
              disabledDate={(current: Dayjs) =>
                watchAll.dateFrom
                  ? current.isBefore(watchAll.dateFrom, 'day')
                  : false
              }
              data-testid="config-logs-date-to-picker"
            />
          </div>

          <div
            className="flex items-center justify-end gap-x-4 pt-4 border-t border-light-input-border dark:border-dark-input-border"
            data-testid="config-logs-filters-actions"
          >
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
              data-testid="config-logs-filters-cancel-btn"
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              type="submit"
              className="min-w-[120px] h-[48px]"
              data-testid="config-logs-filters-save-btn"
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default LogsFilterModal;
